// Додаткові картки користувача у Firebase Realtime Database.
//
// cards/{chatId}/{cardId}:
//   type          — ключ у CARD_DETAILS (kids, white, usd, …)
//   name          — назва картки («Біла картка»)
//   currency      — ISO 4217: UAH | USD | EUR
//   balance       — рядок з двома знаками, як balance/{chatId}/value
//   number        — 16 цифр без пробілів (з контрольною цифрою Луна)
//   expiry, cvv   — «MM/YY», 3 цифри
//   iban          — UA…, 29 символів
//   paymentSystem — 'visa'
//   kind          — 'virtual' | 'plastic'
//   status        — 'active' | 'blocked'
//   dailyLimit    — ліміт оплат за день (рядок, у валюті картки)
//   dailySpent    — витрачено сьогодні з ліміту
//   createdAt     — мс від epoch
// cardTransactions/{chatId}/{cardId}/{txId} — платежі картки, та сама форма,
// що й transactions/{chatId}.
// cardsOrder/{chatId} — порядок карток (масив id, основна — 'black'), як
// їх розставили в «Усі картки»; за ним же йдуть сторінки на головній.
//
// Основна (чорна) картка лишається на старих шляхах users/, balance/,
// transactions/ — їх пише бот.
import { useEffect, useMemo, useState } from 'react';
import { ref, onValue, push, set, update } from 'firebase/database';
import { database } from './firebaseConfig';
import CARD_DETAILS from './cardDetails';
import { readCachedTransactions, cacheTransactions } from './txCache';

const CURRENCY_BY_TYPE = { usd: 'USD', eur: 'EUR' };
export const CURRENCY_SYMBOLS = { UAH: '₴', USD: '$', EUR: '€' };
// Денний ліміт оплат за замовчуванням (у валюті картки), як у monobank
export const DEFAULT_DAILY_LIMIT = { UAH: 2000, USD: 3, EUR: 250 };

// Мініатюри з public/card-icons; для решти — жовта CSS-заглушка
const CARD_ICONS = {
  white: '/card-icons/white.png',
  usd: '/card-icons/usd.png',
  eur: '/card-icons/eur.png',
  cashback: '/card-icons/cashback.png',
};
export const cardIcon = (type) => CARD_ICONS[type] || null;

// digits — знаків після коми (0 — «2 000 ₴», як у ліміті)
export function formatMoney(value, currency = 'UAH', digits = 2) {
  const [intPart, decimalPart] = Number(value || 0).toFixed(digits).split('.');
  const intWithSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const num = decimalPart ? `${intWithSpaces}.${decimalPart}` : intWithSpaces;
  return `${num} ${CURRENCY_SYMBOLS[currency] || currency}`;
}

const digits = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join('');

function luhnDigit(partial) {
  let sum = 0;
  for (let i = 0; i < partial.length; i++) {
    let d = Number(partial[partial.length - 1 - i]);
    if (i % 2 === 0) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return String((10 - (sum % 10)) % 10);
}

// Валютні й біла картки monobank мають BIN 4874, решта — 444111
const BIN_BY_TYPE = { white: '4874', usd: '4874', eur: '4874' };

function generateNumber(type) {
  const bin = BIN_BY_TYPE[type] || '444111';
  const partial = bin + digits(15 - bin.length);
  return partial + luhnDigit(partial);
}

// UA + 2 контрольні + МФО monobank 322001 + рахунок 19 цифр
function generateIban() {
  const bban = `322001000002620${digits(10)}`;
  // «UA00» в кінець, літери → числа (U=30, A=10), mod 97 частинами
  const numeric = `${bban}301000`;
  let mod = 0;
  for (const ch of numeric) mod = (mod * 10 + Number(ch)) % 97;
  return `UA${String(98 - mod).padStart(2, '0')}${bban}`;
}

function generateExpiry() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${month}/${String((d.getFullYear() + 5) % 100).padStart(2, '0')}`;
}

// Записує нову картку і повертає її id
export async function createCard(chatId, type) {
  const detail = CARD_DETAILS[type];
  if (!chatId || !detail) throw new Error(`unknown card type: ${type}`);
  const cardRef = push(ref(database, `cards/${chatId}`));
  const currency = CURRENCY_BY_TYPE[type] || 'UAH';
  await set(cardRef, {
    type,
    name: detail.title,
    currency,
    balance: '0.00',
    number: generateNumber(type),
    expiry: generateExpiry(),
    cvv: digits(3),
    iban: generateIban(),
    paymentSystem: 'visa',
    kind: 'virtual',
    status: 'active',
    dailyLimit: DEFAULT_DAILY_LIMIT[currency].toFixed(2),
    dailySpent: '0.00',
    createdAt: Date.now(),
  });
  return cardRef.key;
}

// «Закрити картку»: прибираємо картку, її платежі й місце в порядку
export function closeCard(chatId, cardId, order = []) {
  if (!chatId || !cardId) return Promise.resolve();
  const updates = {
    [`cards/${chatId}/${cardId}`]: null,
    [`cardTransactions/${chatId}/${cardId}`]: null,
  };
  if (order.includes(cardId)) updates[`cardsOrder/${chatId}`] = order.filter((id) => id !== cardId);
  return update(ref(database), updates);
}

const toList = (val) => (val && typeof val === 'object'
  ? Object.keys(val).map((id) => ({ id, ...val[id] }))
  : []);

const cacheKey = (chatId) => `user_cards_${chatId}`;

function readCache(chatId) {
  try { return JSON.parse(localStorage.getItem(cacheKey(chatId))) || []; } catch (e) { return []; }
}

// Картки користувача (за датою відкриття) разом з їхніми платежами:
// [{ id, type, name, currency, balance, …, transactions: [...] }].
// Стартуємо з кешу, щоб пейджер на головній одразу мав усі сторінки.
export function useUserCards(chatId) {
  const [cards, setCards] = useState(() => readCache(chatId));
  const [txByCard, setTxByCard] = useState({});

  useEffect(() => {
    if (!chatId) return undefined;
    const unsubCards = onValue(ref(database, `cards/${chatId}`), (snap) => {
      const list = toList(snap.val()).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      try { localStorage.setItem(cacheKey(chatId), JSON.stringify(list)); } catch (e) { /* ignore */ }
      setCards(list);
    });
    const unsubTx = onValue(ref(database, `cardTransactions/${chatId}`), (snap) => {
      const val = snap.val() || {};
      const next = {};
      Object.keys(val).forEach((cardId) => { next[cardId] = toList(val[cardId]); });
      setTxByCard(next);
    });
    return () => { unsubCards(); unsubTx(); };
  }, [chatId]);

  return useMemo(
    () => cards.map((c) => ({ ...c, transactions: txByCard[c.id] || [] })),
    [cards, txByCard],
  );
}

export const MAIN_CARD_ID = 'black';

const orderCacheKey = (chatId) => `cards_order_${chatId}`;

// Порядок карток з БД (з кешем — щоб пейджер одразу стояв правильно)
export function useCardsOrder(chatId) {
  const [order, setOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(orderCacheKey(chatId))) || []; } catch (e) { return []; }
  });

  useEffect(() => {
    if (!chatId) return undefined;
    return onValue(ref(database, `cardsOrder/${chatId}`), (snap) => {
      const val = snap.val();
      const list = Array.isArray(val) ? val.filter(Boolean) : [];
      try { localStorage.setItem(orderCacheKey(chatId), JSON.stringify(list)); } catch (e) { /* ignore */ }
      setOrder(list);
    });
  }, [chatId]);

  return order;
}

export function saveCardsOrder(chatId, order) {
  if (!chatId) return Promise.resolve();
  try { localStorage.setItem(orderCacheKey(chatId), JSON.stringify(order)); } catch (e) { /* ignore */ }
  return set(ref(database, `cardsOrder/${chatId}`), order).catch(() => { /* офлайн — Firebase допише сам */ });
}

// Розставляє картки за збереженим порядком; нові (ще не в порядку) — в кінці
export function sortByOrder(cards, order) {
  const pos = new Map(order.map((id, i) => [id, i]));
  return cards
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (pos.get(a.c.id) ?? order.length + a.i) - (pos.get(b.c.id) ?? order.length + b.i))
    .map(({ c }) => c);
}

// ?card=<id> у /history і /transaction/:id — чия виписка відкрита.
// Для основної параметр не додаємо, тож старі посилання ведуть на неї ж.
export const cardQuery = (cardId) => (!cardId || cardId === MAIN_CARD_ID ? '' : `?card=${encodeURIComponent(cardId)}`);
export const cardIdFromSearch = (search) => new URLSearchParams(search).get('card') || MAIN_CARD_ID;

// Виписка однієї картки для «Усі» та деталей транзакції:
// основна — balance/{chatId} і transactions/{chatId},
// додаткова — cards/{chatId}/{cardId} і cardTransactions/{chatId}/{cardId}.
// Стартуємо з кешу, щоб екран одразу був заповнений.
export function useCardStatement(chatId, cardId) {
  const isMain = !cardId || cardId === MAIN_CARD_ID;
  const txCacheId = isMain ? chatId : `${chatId}_${cardId}`;
  const balanceKey = `balance_raw_${chatId}`;

  const [transactions, setTransactions] = useState(() => readCachedTransactions(txCacheId) || []);
  const [card, setCard] = useState(() => (isMain ? null : readCache(chatId).find((c) => c.id === cardId) || null));
  const [mainBalance, setMainBalance] = useState(() => {
    try { return Number(localStorage.getItem(balanceKey)) || 0; } catch (e) { return 0; }
  });

  useEffect(() => {
    if (!chatId) return undefined;
    const txRef = ref(database, isMain ? `transactions/${chatId}` : `cardTransactions/${chatId}/${cardId}`);
    const unsubTx = onValue(txRef, (snap) => {
      const val = snap.val();
      const arr = Array.isArray(val) ? val : toList(val);
      cacheTransactions(txCacheId, arr);
      setTransactions(arr);
    });
    const unsubBalance = isMain
      ? onValue(ref(database, `balance/${chatId}/value`), (snap) => {
        const val = snap.val();
        if (val !== null && val !== undefined) {
          try { localStorage.setItem(balanceKey, String(val)); } catch (e) { /* ignore */ }
        }
        setMainBalance(Number(val) || 0);
      })
      : onValue(ref(database, `cards/${chatId}/${cardId}`), (snap) => {
        setCard(snap.exists() ? { id: cardId, ...snap.val() } : null);
      });
    return () => { unsubTx(); unsubBalance(); };
  }, [chatId, cardId, isMain, txCacheId, balanceKey]);

  return {
    transactions,
    balance: isMain ? mainBalance : Number(card?.balance || 0),
    currency: isMain ? 'UAH' : (card?.currency || 'UAH'),
    card, // null для основної
  };
}
